import { BlisterHeightType, TextFormatEnum } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { DropDownField } from '@/components/shared/CustomField';
import { heightTypeMapping } from '@/constants';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useSetAtom } from 'jotai';
import { PageLoader } from '@/components/shared/PageLoader';

const options = [
  heightTypeMapping[BlisterHeightType.Pdr],
  heightTypeMapping[BlisterHeightType.BlisterTool],
];

export const CalculationMethodSelector = ({
  blisterHeightType,
  setBlisterHeightType,
  updateOverrides,
  setUpdateOverrides,
  loading,
}: {
  blisterHeightType: BlisterHeightType | undefined;
  setBlisterHeightType: (heightType: BlisterHeightType | undefined) => void;
  updateOverrides: boolean;
  setUpdateOverrides: (overrides: boolean) => void;
  loading: boolean;
}) => {
  const setStructureBlisterHeightType = useSetAtom(state.structureBlisterHeightType);

  const [displayValue, setDisplayValue] = useState<string | undefined>(
    blisterHeightType ? heightTypeMapping[blisterHeightType] : undefined
  );

  const handleMethodChange = (value: string) => {
    const newEnumValue = Object.entries(BlisterHeightType).find(
      ([, enumVal]) => heightTypeMapping[enumVal as BlisterHeightType] === value
    )?.[0] as BlisterHeightType | undefined;

    if (newEnumValue) {
      setBlisterHeightType(newEnumValue);
      setDisplayValue(value);
      setStructureBlisterHeightType(newEnumValue);
    }
  };

  return (
    <>
      {!loading && (
        <Stack sx={{ width: '100%', marginTop: '3.2rem' }}>
          <DropDownField
            id="calculation-method"
            title="POI calculation method"
            mode="Edit"
            options={options}
            value={displayValue}
            onFieldValueChange={handleMethodChange}
            disableClearable={true}
            valueFormat={TextFormatEnum.None}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={updateOverrides}
                disableRipple
                onChange={(e) => setUpdateOverrides(e.target.checked)}
                sx={{
                  '& .MuiSvgIcon-root': { fontSize: '1.6rem' },
                  '&.MuiCheckbox-root': { paddingTop: '0.2rem' },
                }}
              />
            }
            label="Update manually overridden calculation methods to match the platform level POI calculation method"
            sx={{
              marginTop: '2.4rem',
              alignItems: 'flex-start',
              '.MuiFormControlLabel-label': { fontSize: '1.4rem', color: 'primary.dark' },
            }}
          />
        </Stack>
      )}
      {loading && (
        <Stack sx={{ width: '100%', marginTop: '3.2rem' }}>
          <Typography variant="h6">
            Calculations are now being performed for all assemblies and points of interest using the
            updated logic. This process may take some time.
          </Typography>
          <Stack justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
            <PageLoader variant="sm" flashing hideProgress />
          </Stack>
        </Stack>
      )}
    </>
  );
};
